import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from "../../../accordion/Accordion";
import { extractCurrentPersonFirma } from "../../../../util/person";
import { PERSON_BY_ID_QUERY } from "../../../../api/person/queries";
import { /*ApolloError,*/ useQuery } from "@apollo/client";
import HistorikStamdata from "./MereOmPerson/HistorikStamdata";
import {
  defaultPersonHistorikColumns,
  defaultPersonKorrespondanceColumns,
  defaultForeningPersonColumns,
  defaultPersonUdvalgColumns,
  defaultPersonHistorik2Columns,
} from "../../../../config/person";
import Medlemskaber from "./MereOmPerson/Medlemskaber";
import Udvalgsposter from "./MereOmPerson/Udvalgsposter";
import Korrespondence from "./MereOmPerson/Korrespondence";
import Abonnementer from "./MereOmPerson/Abonnementer";
import AlternativFakturaAdresse from "./MereOmPerson/AlternativFakturaAdresse";
import useActiveColumns from "../../../../hooks/useActiveColumns";
import Kontaktadresse from "./MereOmPerson/Kontaktadresse";
import { PersonDto, PersonIdKort } from "../../../../interfaces/schema";
import IdKortHistorik, {
  MarkerIdKortSomSendt,
} from "./MereOmPerson/IdKortHistorik";
import Bidragsfaktura from "./MereOmPerson/Bidragsfaktura";
import usePersonChecks from "../../../../hooks/errorHandling/usePersonChecks";
import Censor from "./MereOmPerson/Censor";
import { dateToyyyyMMdd } from "../../../../util/date";
import { differenceInCalendarDays } from "date-fns";
import { useState } from "react";
import { transformQueryDataPerioder } from "../../../../util/apollo";
import HistorikStamdata2 from "./MereOmPerson/HistorikStamdata2";

/**
 * ID-kort-historikken og dens markér-handling kommer som props fra personsiden frem for fra
 * fanens egen forespørgsel.
 *
 * Det er bevidst: markeringen mutérer via personsidens handler, og den opdaterer personsidens
 * tilstand (genhentIdKort) — ikke fanens. Hentede fanen selv feltet, ville tabellen blive
 * stående på Bestilt efter en ekspedering, indtil man skiftede fane. Personsiden henter i
 * forvejen personIdKort til handlingsmenuen, så det koster heller ikke et ekstra felt at læse
 * den derfra — og rækkehandlingen og menupunktet deler dermed både handler og
 * skriveadgangskrav.
 */
export default function MerePerson({
  personId,
  idKortHistorik,
  markerIdKortSomSendt,
}: {
  personId: string;
  idKortHistorik: PersonIdKort[];
  markerIdKortSomSendt?: MarkerIdKortSomSendt;
}) {
  const [personHistorikColumns, setPersonHistorikActiveColumns] =
    useActiveColumns(
      "person-personhistorik-active-columns",
      defaultPersonHistorikColumns,
    );
  const [personHistorik2Columns, setPersonHistorik2ActiveColumns] =
    useActiveColumns(
      "person-personhistorik2-active-columns",
      defaultPersonHistorik2Columns,
    );
  const [personKorrespondanceColumns, setPersonKorrespondanceActiveColumns] =
    useActiveColumns(
      "person-personkorrespondance-active-columns",
      defaultPersonKorrespondanceColumns,
    );
  const [foreningPersonColumns, setForeningPersonActiveColumns] =
    useActiveColumns(
      "person-personforening-active-columns",
      defaultForeningPersonColumns,
    );
  const [personUdvalgColumns, setPersonUdvalgActiveColumns] = useActiveColumns(
    "person-personudvalg-active-columns",
    defaultPersonUdvalgColumns,
  );

  const [data, setData] = useState<
    | {
        person: PersonDto;
      }
    | undefined
  >(undefined);
  const { loading, error, refetch, networkStatus } = useQuery<{
    person: PersonDto;
  }>(
    PERSON_BY_ID_QUERY("personByIdMere", {
      personKontaktadresse: true,
      personKorrespondance: true,
      foreningPerson: true,
      personUdvalg: true,
      personhistorikNavigation: true,
      personhistorik2Navigation: true,
      personAbonnement: true,
      personBetalingsadresse: true,
      personBidragsopkraevning: true,
      fmuretssagsproeveCensor: true,
    }),
    {
      variables: { id: personId, dato: dateToyyyyMMdd(new Date()) },
      notifyOnNetworkStatusChange: true,
      onCompleted: (d) => {
        const transformed = transformQueryDataPerioder(d);
        setData(transformed);
      },
    },
  );

  const { checkResult, person } = usePersonChecks(
    loading,
    error,
    data,
    networkStatus,
  );
  if (checkResult) return checkResult;

  const currentFirma = extractCurrentPersonFirma(person);
  const currentAlternativAdresse = person.personBetalingsadresse?.find(
    (a) =>
      differenceInCalendarDays(
        new Date(a.periode.tilDatoInklusiv),
        new Date(),
      ) >= 0,
  );
  const currentAdresse = person.adresse?.adresse;
  return (
    <div className="container">
      <div className="row gy-3">
        <div className="col d-flex align-items-stretch">
          <div className="c-card">
            <h3 className="mb-3 mt-2">Adresse</h3>
            <div
              className={
                currentFirma || currentAdresse
                  ? "c-item-list"
                  : "d-flex align-items-center justify-content-center"
              }
            >
              {currentFirma ? (
                <div
                  className="form-control--value"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {currentFirma.firma.navn1} <br />
                  {currentFirma.firma.beliggenhedsadresse.toLines.join("\n")}
                </div>
              ) : currentAdresse ? (
                <div
                  className="form-control--value"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {currentAdresse.toLines.join("\n")}
                </div>
              ) : (
                <p className="text-muted">Ingen adresse at vise</p>
              )}
            </div>
          </div>
        </div>

        <div className="col d-flex align-items-stretch">
          <div className="c-card">
            <div className="col mt-2">
              <h3 className="mb-3 mt-2">Fakturering</h3>
              <div className="c-item-list">
                <div>
                  <h5 className="form-label">Faktura afsend standard</h5>
                  <div className="form-control--value">
                    {currentFirma?.firma.fakturaAfsendelseNavigation?.navn ??
                      "-"}
                  </div>
                </div>
                <div>
                  <h5 className="form-label">Alternativ faktureringsadresse</h5>
                  <div className="form-control--value">
                    {currentAlternativAdresse
                      ? currentAlternativAdresse.adresse.toLines.join(" ")
                      : "-"}
                  </div>
                </div>
                <div>
                  <h5 className="form-label">EAN-nr.</h5>
                  <div className="form-control--value">
                    {currentFirma?.firma.ean ?? "-"}
                  </div>
                </div>
                <div>
                  <h5 className="form-label">E-mail, faktura</h5>
                  <div className="form-control--value">
                    {currentFirma?.firma.fakturaEmail ?? "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AccordionRoot localstorageKey="person-id-mereperson-accordion-value">
        <AccordionItem
          className="c-card c-card--accordion"
          value="censor"
          isEmpty={!person.fmuretssagsproeveCensor?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Censor</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Censor person={person} />
            <div className="py2-">&nbsp;</div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="korrespondance"
          isEmpty={!person.personKorrespondance?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Korrespondance</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Korrespondence
              invalidateData={refetch}
              personId={person.id}
              columns={personKorrespondanceColumns}
              setActiveColumns={setPersonKorrespondanceActiveColumns}
              personKorrespondance={person.personKorrespondance ?? []}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="bidragsfaktura"
          isEmpty={!person.personBidragsopkraevning?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Bidragsfaktura</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Bidragsfaktura person={person} invalidateData={refetch} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="medlemskaber"
          isEmpty={!person.foreningPerson?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Medlemskaber</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Medlemskaber
              invalidateData={refetch}
              person={person}
              foreningPersonColumns={foreningPersonColumns}
              setForeningPersonActiveColumns={setForeningPersonActiveColumns}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="udvalgsposter"
          isEmpty={!person.personUdvalg?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Udvalgsposter</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Udvalgsposter
              invalidateData={refetch}
              personId={person.id}
              personUdvalg={person.personUdvalg ?? []}
              columns={personUdvalgColumns}
              setActiveColumns={setPersonUdvalgActiveColumns}
            />
          </AccordionContent>
        </AccordionItem>
        <HistorikStamdata
          personHistorik={person.personhistorikNavigation ?? []}
          personHistorikColumns={personHistorikColumns}
          setPersonHistorikActiveColumns={setPersonHistorikActiveColumns}
        />
        <HistorikStamdata2
          personHistorik={person.personhistorik2Navigation ?? []}
          personHistorikColumns={personHistorik2Columns}
          setPersonHistorikActiveColumns={setPersonHistorik2ActiveColumns}
        />
        <AccordionItem
          className="c-card c-card--accordion"
          value="abonnementer"
          isEmpty={!person.personAbonnement?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Abonnementer</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Abonnementer invalidateData={refetch} person={person} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="kontaktadresse"
          isEmpty={!person.personKontaktadresse?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Kontaktadresse</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Kontaktadresse person={person} refetch={refetch} />
          </AccordionContent>
        </AccordionItem>
        <AlternativFakturaAdresse person={person} refetch={refetch} />
        {/* Nederst efter kundens ønske (ADV-234): receptionen åbner fanen for at ekspedere en
            kortbestilling, og vil have punktet samme sted hver gang, under faktureringen. */}
        <AccordionItem
          className="c-card c-card--accordion"
          value="idkort"
          isEmpty={!idKortHistorik.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>ID-kort</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <IdKortHistorik
              historik={idKortHistorik}
              kundeNr={person.kundeNr}
              markerSomSendt={markerIdKortSomSendt}
            />
          </AccordionContent>
        </AccordionItem>
      </AccordionRoot>
    </div>
  );
}
